import { useRef, useState } from 'react';

import {
  BoundingBox,
  PieceAnnotation,
  TrackingNumberValidationStatus,
} from '@apiContract';
import { PieceAnnotations } from '@components/Capture/types';
import { useOpenCvTools } from '@hooks/useOpenCvTools';
import isEqual from 'lodash/fp/isEqual';

import { BarcodesStatusesCounts, getBarcodesStatusesCounts } from '@utils/annotations';

interface ImagePreviewProps {
  imageDataUrl?: string;
  annotations?: PieceAnnotations;
  width?: number;
  height?: number;
  onAnnotationClick?: () => void;
  croppingBox?: BoundingBox;
  isDrawAnnotations?: boolean;
}

enum AnnotationColor {
  Red = 0,
  Green = 1,
  Gray = 2,
  Yellow = 3,
}

type ImgRef = {
  imageDataUrl?: string;
  imgObject?: HTMLImageElement;
};

const colorByAnnotation = {
  [AnnotationColor.Green]: '104, 186, 63',
  [AnnotationColor.Red]: '255, 78, 43',
  [AnnotationColor.Gray]: '0, 0, 0',
  [AnnotationColor.Yellow]: '249, 200, 78',
};

export type SetPreviewPaneDimensions = (width: number, height: number) => void;

const getAnnotationColor = (annotation: PieceAnnotation, barcodeStatuses: BarcodesStatusesCounts) => {
  const { validationStatus } = annotation.pieceAnnotationsTrackingNumber || {};
  if (validationStatus === TrackingNumberValidationStatus.None) {
    return AnnotationColor.Gray;
  }

  if (validationStatus === TrackingNumberValidationStatus.Valid) {
    return AnnotationColor.Green;
  }

  if (barcodeStatuses[TrackingNumberValidationStatus.Valid]) {
    return AnnotationColor.Gray;
  }

  if (barcodeStatuses[TrackingNumberValidationStatus.BadFormat]) {
    return AnnotationColor.Yellow;
  }

  return AnnotationColor.Red;
};

const drawAnnotations = (
  barcodeStatuses: BarcodesStatusesCounts,
  ctx?: CanvasRenderingContext2D | null,
  annotations?: PieceAnnotations,
) => {
  if (!ctx) return [];
  const paths: Path2D[] = [];
  annotations?.items?.forEach((annotation) => {
    if (annotation.boundingBox) {
      const rgb = colorByAnnotation[getAnnotationColor(annotation, barcodeStatuses)];
      const coords = annotation.boundingBox;
      if (!coords) return;

      ctx.strokeStyle = `rgba(${rgb})`;
      ctx.fillStyle = `rgba(${rgb})`;
      ctx.setLineDash([10, 7]);
      ctx.lineWidth = 5;

      ctx.beginPath();
      ctx.moveTo(coords.topLeftX, coords.topLeftY);
      ctx.lineTo(coords.topRightX, coords.topRightY);
      ctx.lineTo(coords.bottomRightX, coords.bottomRightY);
      ctx.lineTo(coords.bottomLeftX, coords.bottomLeftY);
      ctx.closePath();
      ctx.stroke();

      ctx.fillStyle = `rgba(${rgb}, 0.5)`;
      const padding = 2;
      const fillCoords = {
        topLeftX: coords.topLeftX + padding,
        topLeftY: coords.topLeftY + padding,
        topRightX: coords.topRightX - padding,
        topRightY: coords.topRightY + padding,
        bottomRightX: coords.bottomRightX - padding,
        bottomRightY: coords.bottomRightY - padding,
        bottomLeftX: coords.bottomLeftX + padding,
        bottomLeftY: coords.bottomLeftY - padding,
      };

      const path = new Path2D();
      path.moveTo(fillCoords.topLeftX, fillCoords.topLeftY);
      path.lineTo(fillCoords.topRightX, fillCoords.topRightY);
      path.lineTo(fillCoords.bottomRightX, fillCoords.bottomRightY);
      path.lineTo(fillCoords.bottomLeftX, fillCoords.bottomLeftY);
      ctx.fill(path);

      paths.push(path);

      const centerX = (coords.topLeftX + coords.topRightX) / 2;
      const centerY = (coords.topLeftY + coords.bottomLeftY) / 2;

      const text = annotation.textValue;

      if (text) {
        ctx.font = '26px Public Sans';
        const textMetrics = ctx.measureText(text || '');
        const textWidth = textMetrics.width;
        const textHeight = 30;

        const bgX = centerX - textWidth / 2 - 5;
        const bgY = centerY - textHeight / 2 - 5;
        const bgWidth = textWidth + 20;
        const bgHeight = textHeight + 10;
        const radius = bgHeight / 2;

        ctx.fillStyle = `rgba(${rgb})`;
        ctx.beginPath();
        ctx.moveTo(bgX + radius, bgY);
        ctx.lineTo(bgX + bgWidth - radius, bgY);
        ctx.arc(bgX + bgWidth - radius, bgY + radius, radius, -0.5 * Math.PI, 0.5 * Math.PI, false);
        ctx.lineTo(bgX + radius, bgY + bgHeight);
        ctx.arc(bgX + radius, bgY + radius, radius, 0.5 * Math.PI, 1.5 * Math.PI, false);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = 'white';
        ctx.fillText(text || '', centerX + 3 - textWidth / 2, centerY + textHeight / 4);
      }
    }
  });
  return paths;
};

const clear = (ctx?: CanvasRenderingContext2D | null, canvasRef?: React.RefObject<HTMLCanvasElement | null>) => {
  if (!ctx || !canvasRef?.current) return;
  ctx.clearRect(0, 0, canvasRef.current.width || 0, canvasRef.current.height || 0);
};

const convertAnnotations = (
  getTransformedInnerQuad: any,
  annotations?: PieceAnnotations,
  croppingBox?: BoundingBox,
) => {
  if (!croppingBox || !annotations) return annotations;
  return {
    ...annotations,
    items: annotations.items?.reduce<PieceAnnotation[]>((acc, item) => {
      acc.push(
        item.boundingBox
          ? {
              ...item,
              boundingBox: getTransformedInnerQuad(croppingBox, item.boundingBox),
            }
          : item,
      );
      return acc;
    }, []),
  };
};

export const ImagePreview = ({
  imageDataUrl,
  width,
  height,
  annotations,
  onAnnotationClick,
  croppingBox,
  isDrawAnnotations = true,
}: ImagePreviewProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<ImgRef | null>(null);
  const annotationRef = useRef<PieceAnnotations | null>(null);
  const [paths, setPaths] = useState<Path2D[]>([]);
  const { getTransformedInnerQuad } = useOpenCvTools();

  const statuses = getBarcodesStatusesCounts(annotations?.items || []);

  const transformAnnotations = convertAnnotations(getTransformedInnerQuad, annotations, croppingBox);

  if (transformAnnotations && isDrawAnnotations && !isEqual(transformAnnotations, annotationRef.current)) {
    annotationRef.current = transformAnnotations;
    if (imgRef.current?.imgObject) {
      const ctx = canvasRef.current?.getContext('2d');
      clear(ctx, canvasRef);
      ctx?.drawImage(imgRef.current.imgObject, 0, 0);
      setPaths(drawAnnotations(statuses, ctx, transformAnnotations));
    }
  }

  if (imageDataUrl && imageDataUrl !== imgRef.current?.imageDataUrl) {
    const img = document.createElement('img');
    img.src = imageDataUrl;
    imgRef.current = { imageDataUrl };
    img.onload = () => {
      imgRef.current = imgRef.current ? { ...imgRef.current, imgObject: img } : { imgObject: img };
      if (!canvasRef.current) return;
      canvasRef.current.width = img.width;
      canvasRef.current.height = img.height;
      const ctx = canvasRef.current.getContext('2d');
      clear(ctx, canvasRef);
      ctx?.drawImage(img, 0, 0);
      if (annotationRef.current?.items && isDrawAnnotations) {
        setPaths(drawAnnotations(statuses, ctx, annotationRef.current));
      }
    };
  }

  const handleClickOrHover = (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const canvas = canvasRef.current;
    if (!canvas || !onAnnotationClick) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    paths.some((path) => {
      if (ctx.isPointInPath(path, x, y)) {
        onAnnotationClick();
      }
    });
  };

  return (
    <div
      className="rd-image-preview"
      style={{
        display: 'flex',
        width,
        height,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <canvas
        style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', cursor: 'default' }}
        ref={canvasRef}
        onClick={(e) => handleClickOrHover(e)}
      />
    </div>
  );
};
